import React from 'react';

import TiebreakWinMark from './tiebreak-win-mark';
import {
  deltaTone,
  formatBuchholz,
  formatDelta,
  smallPointsBalance,
  type StandingRow,
} from './scoring-types';

interface Props {
  rows: StandingRow[];
  showPoints: boolean;
  showPlacement?: boolean;
  doubles?: boolean;
  testId?: string;
  // Czym rozstrzygany jest remis — czytający ma to zobaczyć bez pytania nikogo.
  tiebreakHint?: string;
  // Ile punktów daje pauza w americano — kolumna pokazuje się tylko, gdy ktoś pauzował.
  restingRunPoints?: number | null;
  // Duży widok na telefon: tylko miejsce, nazwa i punkty główne — reszta w oknie gracza.
  compact?: boolean;
  onPickPlayer?: (row: StandingRow) => void;
}

export const ScoringStandings: React.FC<Props> = ({
  rows,
  showPoints,
  showPlacement = true,
  doubles = false,
  testId = 'scoring-standings',
  tiebreakHint,
  restingRunPoints = null,
  compact = false,
  onPickPlayer,
}) => {
  if (rows.length === 0) {
    return null;
  }

  const showFullColumns = !compact;
  const showRests = showFullColumns && restingRunPoints !== null && rows.some((row) => (row.restedRuns ?? 0) > 0);
  const showDraws = showFullColumns && rows.some((row) => row.draws != null);
  const showBuchholz = showFullColumns && rows.some((row) => row.buchholz != null);

  function onRowKeyDown(event: React.KeyboardEvent, row: StandingRow): void {
    if (!onPickPlayer || (event.key !== 'Enter' && event.key !== ' ')) {
      return;
    }

    event.preventDefault();
    onPickPlayer(row);
  }

  return (
    <div className={compact ? 'scoring-standings scoring-standings--compact' : 'scoring-standings'} data-testid={testId}>
      <table className="scoring-standings__table">
        <thead>
          <tr>
            <th
              className="scoring-standings__rank"
              data-testid={`${testId}-tiebreak`}
              title={tiebreakHint ?? 'Miejsce w grupie'}
            >
              #
            </th>
            <th>{doubles ? 'Para' : 'Zawodnik'}</th>
            {showFullColumns && <th title="Rozegrane mecze">M</th>}
            {(showFullColumns || !showPoints) && <th title="Wygrane mecze">W</th>}
            {showDraws && <th title="Remisy">R</th>}
            {showFullColumns && <th title="Sety wygrane : przegrane">Sety</th>}
            {showFullColumns && <th title="Bilans lotek — małe punkty zdobyte minus stracone">Lotki</th>}
            {showRests && (
              <th title={`Rundy przesiedziane na pauzie — każda liczy się jak ${restingRunPoints} pkt`}>
                Pauzy
              </th>
            )}
            {showPoints && (
              <th title="Punkty za sety wg systemu punktacji tej grupy">Pkt</th>
            )}
            {showBuchholz && <th title="Buchholz — suma punktów przeciwników">Bh</th>}
            {showFullColumns && showPlacement && (
              <th title="Punkty do klasyfikacji sezonu za miejsce w grupie — 1. miejsce w pierwszej grupie = 300, każde kolejne −10">
                Klas.
              </th>
            )}
            {showFullColumns && (
              <th title="Ranking po tej kolejce (szacunkowo) i zmiana, jaką ta kolejka przyniosła">
                RTG
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.leaguePlayerId}
              data-testid={`standing-${row.leaguePlayerId}`}
              className={onPickPlayer ? 'scoring-standings__row--pickable' : undefined}
              role={onPickPlayer ? 'button' : undefined}
              tabIndex={onPickPlayer ? 0 : undefined}
              title={onPickPlayer ? `Mecze — ${row.name}` : undefined}
              onClick={onPickPlayer ? () => onPickPlayer(row) : undefined}
              onKeyDown={(event) => onRowKeyDown(event, row)}
            >
              <td className="scoring-standings__rank">
                {row.decidedBy && (
                  <TiebreakWinMark
                    decision={row.decidedBy}
                    name={row.name}
                    testId={`tiebreak-win-${row.leaguePlayerId}`}
                  />
                )}
                {row.rank}
              </td>
              <td className="scoring-standings__name">{row.name}</td>
              {showFullColumns && <td>{row.played}</td>}
              {(showFullColumns || !showPoints) && <td className="scoring-standings__wins">{row.wins}</td>}
              {showDraws && (
                <td className="scoring-standings__draws" data-testid={`standing-draws-${row.leaguePlayerId}`}>
                  {row.draws ?? 0}
                </td>
              )}
              {showFullColumns && <td className="scoring-standings__sets">{row.setsWon}:{row.setsLost}</td>}
              {showFullColumns && (
                <td className={`scoring-standings__small ${deltaTone(smallPointsBalance(row))}`}>
                  {formatDelta(smallPointsBalance(row))}
                </td>
              )}
              {showRests && (
                <td className="scoring-standings__rests" data-testid={`standing-rests-${row.leaguePlayerId}`}>
                  {row.restedRuns ?? 0}
                </td>
              )}
              {showPoints && <td className="scoring-standings__points">{row.points}</td>}
              {showBuchholz && (
                <td className="scoring-standings__buchholz" data-testid={`standing-buchholz-${row.leaguePlayerId}`}>
                  {formatBuchholz(row.buchholz ?? 0)}
                </td>
              )}
              {showFullColumns && showPlacement && (
                <td className="scoring-standings__placement" data-testid={`standing-placement-${row.leaguePlayerId}`}>
                  {row.placementPoints}
                </td>
              )}
              {showFullColumns && (
                <td className="scoring-standings__rating" data-testid={`standing-rating-${row.leaguePlayerId}`}>
                  {row.rating === null ? '—' : Math.round(row.rating)}
                  {row.ratingDelta != null && (
                    <span
                      className={`scoring-standings__rating-delta ${deltaTone(row.ratingDelta)}`}
                      data-testid={`standing-rating-delta-${row.leaguePlayerId}`}
                    >
                      {formatDelta(Math.round(row.ratingDelta))}
                    </span>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ScoringStandings;
